import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLang, type Lang } from '../../../i18n';
import { get } from '../../../services/api';
import useIntersectionFadeIn from '../../../hooks/useIntersectionFadeIn';

interface Post {
  slug: string;
  title: string;
  excerpt?: string;
  cover?: string;
  reading_time?: number;
  tag?: string;
}
interface BlogApi { items?: Post[]; results?: Post[] }

const LANG_MAP: Record<Lang, string> = { 'uz-Latn': 'uz_lat', 'uz-Cyrl': 'uz_cyr', ru: 'ru', en: 'en' };

export default function BlogTeaserSection() {
  const { t, lang } = useLang();
  const [items, setItems] = useState<Post[]>([]);
  const rootRef = useRef<HTMLElement>(null);
  const visible = useIntersectionFadeIn(rootRef);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await get<BlogApi>(`/blog/?limit=3&lang=${LANG_MAP[lang]}`);
        if (cancel) return;
        const list = res?.items || res?.results || [];
        setItems(list.slice(0, 3));
      } catch {
        if (!cancel) setItems([]);
      }
    })();
    return () => { cancel = true; };
  }, [lang]);

  if (!items.length) return null;

  return (
    <section ref={rootRef} className={`section blog scroll-fade-in${visible ? ' scroll-fade-in--visible' : ''}`}>
      <div className="container">
        <header className="section-header">
          <span className="eyebrow">{t('home.blog.eyebrow')}</span>
          <h2>{t('home.blog.title')}</h2>
          <p className="lead">{t('home.blog.sub')}</p>
        </header>

        <div className="blog__grid">
          {items.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="blog__card blog__card--text card-frame card-hover">
              <div className="blog__body blog__body--full">
                <div className="blog__meta">
                  {p.tag ? <span className="blog__tag">{p.tag}</span> : null}
                  {p.reading_time ? (
                    <span className="blog__time">{t('blog.reading_time', { minutes: p.reading_time })}</span>
                  ) : null}
                </div>
                <h4 className="blog__title">{p.title}</h4>
                {p.excerpt ? <p className="blog__excerpt blog__excerpt--lg">{p.excerpt}</p> : null}
                <span className="blog__more-link blog__more-link--inline">
                  {t('home.see_all')} <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="blog__more">
          <Link to="/blog" className="blog__more-link">
            {t('home.see_all')} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
